import type { Store } from 'effector'
import type { RouterResponse } from '~/core/router'
import type {
  Context,
  RouteData,
  RouteHistory,
} from '../../model/player/index.h'

import { createEvent, createStore, sample } from 'effector'
import { previous } from 'patronum'
import { hooks } from '~/core/router'
import { STATIC_IDENTIFIERS } from '~/shared/appearance'
import { aye } from '~/shared/helpers'
import { KEY_MAP } from '~/shared/routes/query'
import { DEFAULT } from '../../model/player/index.h'
import {
  checkPropertyByDepth,
  getPartsFromHistory,
  getPropertyByDepth,
  IDENTIFIER_TO_ANALYTIX,
  MODULE_NAMES_BY_IDENTIFIER,
  mergePartSToString,
} from '../../model/player/lib'
import { navbarRedirect } from '../../model/player/routes'

export const $openFrom = createStore('')

/**
 * Route-history resolver used to produce `route`
 * for `ui_action` analytics events.
 */

const updateRoute = createEvent<RouterResponse>()
const foundModule = createEvent<{ name: string; route: RouterResponse }>()
const setNewModule = createEvent<string>()
const setNewCustomModule = createEvent<{ from: string; to: string }>()
const routeInited = createEvent<RouteData>()
export const setHistory = createEvent<{
  history: RouteHistory
  route?: RouterResponse
}>()

hooks.register(hooks.Init, routeInited)

const $latestModule = createStore('').on(
  setNewModule,
  (_, newModule) => IDENTIFIER_TO_ANALYTIX[newModule] ?? newModule
)

const $customModule = createStore<{ from: string; to: string } | null>(null)
  .on(setNewCustomModule, (_, newModule) => newModule)
  .on(setNewModule, (state, newModule) =>
    state?.from === newModule ? state : null
  ) // clear on completely new module
  .reset(navbarRedirect)

// Current module history deep level
const $deep = createStore<number>(0)
  .on(setHistory, (state, _ignore) => state + 1)
  .on(setNewModule, () => 0)

/*
 * This sample keeps track of latest user visited module name.
 * 'setNewModule' called on root module pages
 * and resets deep level - so history from previous module ignored.
 */
sample({
  clock: updateRoute,
  source: $latestModule,
  filter: (_, { __resolved }) => {
    const resolved = __resolved as Context
    if (checkPropertyByDepth(resolved, '/modules', 3)) return true
    if (checkPropertyByDepth(resolved, '/:identifier', 1)) return true
    return false
  },
  fn: (latestModule, route) => {
    const { __resolved, props } = route
    const resolved = __resolved as Context
    const path = getPropertyByDepth(
      resolved,
      1,
      MODULE_NAMES_BY_IDENTIFIER[STATIC_IDENTIFIERS.HOME]
    )

    if (checkPropertyByDepth(resolved, '/:identifier', 1))
      return { name: props.identifier, route }
    if (checkPropertyByDepth(resolved, '/modules', 3))
      return { name: path.slice(1), route }

    return { name: latestModule, route }
  },
  target: foundModule,
})

sample({
  clock: foundModule,
  fn: ({ name }) => name,
  target: setNewModule,
})

/*
 * Save latest custom module
 */
sample({
  clock: foundModule,
  filter: ({ route }) => {
    const query = route?.props?.query
    return aye(query) && (query.has('module') || query.has(KEY_MAP.module))
  },
  fn: ({ name, route }) => {
    const query = route.props!.query as URLSearchParams
    return {
      from: name,
      to: query.get('module') || query.get(KEY_MAP.module) || '',
    }
  },
  target: setNewCustomModule,
})

/*
 * This sample validates and forwards route and its init values to analytix feature
 */
sample({
  clock: routeInited,
  filter: (data) => aye(data) && aye(data.route) && aye(data.route.__resolved),
  fn: ({ route }) => route,
  target: updateRoute,
})

const $history = createStore<RouteHistory>(DEFAULT).on(
  setHistory,
  (_, data) => data.history
)

/*
 * Merge module name with route data and init values
 */
sample({
  clock: updateRoute,
  source: $latestModule,
  filter: (_ignore, route) => aye(route?.props?.query),
  fn: (module, route) => {
    const meta = route?.meta

    const query = route.props.query as URLSearchParams

    const routeName =
      query.get('routeName') || query.get(KEY_MAP.routeName) || meta?.title
    const categoryName =
      query.get('categoryName') || query.get(KEY_MAP.categoryName)

    return {
      history: { module, routeName, categoryName },
      route,
    }
  },
  target: setHistory,
})

const $previous: Store<RouteHistory> = previous($history, DEFAULT)

const $beforePrevious: Store<RouteHistory> = previous($previous, DEFAULT)

const openFromUpdated = createEvent<RouteHistory>()

/*
 * Generate open from string on any route change
 */
sample({
  clock: setHistory,
  source: {
    current: $history,
    previous: $previous,
    beforePrevious: $beforePrevious,
    deep: $deep,
    // overrides
    customModule: $customModule,
  },
  fn: (source) => {
    const parts = getPartsFromHistory(source)

    const { customModule } = source

    return {
      module: customModule ? customModule.to : parts.module,
      routeName: parts.routeName,
      categoryName: parts.categoryName,
    }
  },
  target: openFromUpdated,
})

sample({
  clock: openFromUpdated,
  fn: mergePartSToString,
  target: $openFrom,
})
